import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, Car, Award, CheckCircle2, Sparkles, Circle, ArrowRight, MapPin } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/hooks/useSeo";

const TEST_VISUALS = {
  canada_citizenship: { icon: ShieldCheck, color: "#D92D20", bg: "#FEF2F2", emoji: "🇨🇦" },
  alberta_class7:     { icon: Car,         color: "#064E3B", bg: "#D1FAE5", emoji: "🚦" },
  ontario_g1:         { icon: Car,         color: "#1E40AF", bg: "#DBEAFE", emoji: "🚗" },
};

const HIGHLIGHTS = [
  "100% Free",
  "No Subscription Required",
  "Unlimited Practice Tests",
  "Instant Results",
  "Detailed Explanations",
  "Progress Tracking",
  "Designed for Canadian Learners",
];

export default function PracticeLanding() {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [online, setOnline] = useState(null);

  useSeo({
    title: "Free Practice Tests for Canada — Citizenship, G1 & Class 7 | TeachMeOnline.ca",
    description: "Free unlimited practice tests for the Canadian Citizenship exam, Ontario G1, and Alberta Class 7 learner permit. Instant scoring, explanations, and progress tracking. No credit card required.",
    canonical: "https://teacher4me.ca/practice",
    image: "https://teacher4me.ca/tmo-logo.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Free Canadian Practice Tests",
      "itemListElement": [
        { "@type": "ListItem", position: 1, name: "Canada Citizenship Test Practice", url: "https://teacher4me.ca/practice/canada-citizenship" },
        { "@type": "ListItem", position: 2, name: "Alberta Class 7 Knowledge Test", url: "https://teacher4me.ca/practice/alberta-class-7" },
        { "@type": "ListItem", position: 3, name: "Ontario G1 Knowledge Test",     url: "https://teacher4me.ca/practice/ontario-g1" },
      ],
    },
  });

  useEffect(() => {
    api.get("/api/practice/tests").then(({ data }) => setTests(data.tests || []));
    api.get("/api/widgets/online-users?scope=practice").then(({ data }) => setOnline(data.count));
  }, []);

  return (
    <div data-testid="practice-landing">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#FEF2F2] to-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#064E3B] text-white text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> 100% Free · No credit card
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl mt-3 tracking-tight max-w-4xl">
            Free Practice Tests for Canada
          </h1>
          <p className="text-[#4B5563] mt-4 max-w-2xl text-lg">
            Prepare for your exams with <b>free practice questions, instant scoring,
            explanations, progress tracking, and unlimited practice opportunities.</b>
          </p>
          {online != null && (
            <div data-testid="practice-online" className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#064E3B] bg-[#D1FAE5] px-3 py-1.5 rounded-full font-semibold">
              <Circle className="w-2.5 h-2.5 fill-[#10B981] text-[#10B981] animate-pulse" />
              {online} learners practicing right now
            </div>
          )}
          <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#4B5563]">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#064E3B]" />{h}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TEST CARDS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="label-eyebrow">Choose your test</div>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-1">3 free practice modules — start now.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {tests.map((t) => {
            const v = TEST_VISUALS[t.key] || { icon: Award, color: "#D92D20", bg: "#FEF2F2", emoji: "📝" };
            const Icon = v.icon;
            return (
              <Link key={t.key} to={`/practice/${t.slug}`} data-testid={`practice-card-${t.key}`}
                className="group bg-white border border-[#E5E7EB] rounded-3xl overflow-hidden card-lift">
                <div className="p-6" style={{ background: v.bg }}>
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center"
                      style={{ color: v.color }}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-[#064E3B] text-white px-2 py-1 rounded-full">
                      FREE
                    </span>
                  </div>
                  <div className="text-4xl mt-3">{v.emoji}</div>
                </div>
                <div className="p-6">
                  <div className="font-display font-bold text-lg leading-tight">{t.name}</div>
                  <div className="text-xs text-[#4B5563] mt-1">{t.short}</div>
                  <p className="text-sm text-[#4B5563] mt-3">{t.blurb}</p>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-[#F9FAFB] rounded-lg p-2">
                      <div className="font-bold text-base text-[#111827]" data-testid={`q-count-${t.key}`}>{t.question_count}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">Questions</div>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-lg p-2">
                      <div className="font-bold text-base text-[#111827]">{t.categories.length}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">Categories</div>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-lg p-2">
                      <div className="font-bold text-base text-[#111827]">{t.passing_score}%</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">To pass</div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm font-semibold text-[#D92D20] inline-flex items-center gap-1 group-hover:underline">
                    Start practicing <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CONVERSION TO TUTORING */}
      <section className="bg-[#064E3B] text-white">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <div className="text-xs uppercase tracking-widest text-white/70 font-bold">Need help studying?</div>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-2">Try Teacher4Me AI Tutoring</h2>
          <p className="text-sm text-white/80 mt-2 max-w-xl mx-auto">
            Unlimited AI tutor questions · worksheets · quizzes · parent reports. <b>Free Forever plan</b> available.
          </p>
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <Link to={user ? "/tutor" : "/tutor-landing"} data-testid="practice-conversion-cta"
              className="btn-primary inline-flex items-center gap-2">
              Start Free Tutoring <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/tutor-landing" data-testid="practice-conversion-learn"
              className="text-sm border border-white/30 px-5 py-3 rounded-full font-semibold hover:bg-white/10">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-12 text-center">
        <MapPin className="w-8 h-8 text-[#D92D20] mx-auto" />
        <div className="font-display font-bold text-2xl mt-3">Built for Canadians</div>
        <p className="text-sm text-[#4B5563] mt-2">
          Every question is contextualized for Canadian learners — citizenship law, provincial road
          rules, real-world scenarios. No US-centric content, no generic test prep.
        </p>
      </section>
    </div>
  );
}
